import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NavigationBlockProvider } from './context/NavigationBlockContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import Classes from './pages/Classes';
import Students from './pages/Students';
import StudentDetail from './pages/StudentDetail';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import CompanyEdit from './pages/CompanyEdit';
import MyCompany from './pages/MyCompany';
import Certificates from './pages/Certificates';
import CertificateTemplates from './pages/CertificateTemplates';
import Declarations from './pages/Declarations';
import Users from './pages/Users';
import ChangePassword from './pages/ChangePassword';
import Settings from './pages/Settings';
import AdminSettings from './pages/AdminSettings';
import TrainingCenters from './pages/TrainingCenters';
import TrainingCenterDetail from './pages/TrainingCenterDetail';
import Instructors from './pages/Instructors';
import InstructorDetail from './pages/InstructorDetail';
import ClassForm from './pages/ClassForm';
import SecurityManagement from './pages/SecurityManagement';
import CompanyCategories from './pages/CompanyCategories';
import Reports from './pages/Reports';
import EmailMarketing from './pages/EmailMarketing';
import EmailSettings from './pages/EmailSettings';
import AuditLogs from './pages/AuditLogs';
import ExpiryReminders from './pages/ExpiryReminders';
import ExpiryReminderSettings from './pages/ExpiryReminderSettings';
import ExpiryReminderHistory from './pages/ExpiryReminderHistory';
import ExpiryReminderHistoryDetail from './pages/ExpiryReminderHistoryDetail';
import BackupRestore from './pages/BackupRestore';
import MergeDuplicates from './pages/MergeDuplicates';
import MergeHistory from './pages/MergeHistory';
import Unsubscribe from './pages/Unsubscribe';
import CampaignDetail from './pages/CampaignDetail';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 bg-[#2C9DE1] rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-xl">EM</span>
          </div>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user must change password, redirect to change password page
  if (user?.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 bg-[#2C9DE1] rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-xl">EM</span>
          </div>
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      
      {/* Password Change (special route - accessible when logged in but must change password) */}
      <Route path="/change-password" element={<ChangePassword />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/training-centers" element={<ProtectedRoute><TrainingCenters /></ProtectedRoute>} />
      <Route path="/training-centers/:id" element={<ProtectedRoute><TrainingCenterDetail /></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
      <Route path="/classes" element={<ProtectedRoute><Classes /></ProtectedRoute>} />
      <Route path="/classes/new" element={<ProtectedRoute><ClassForm /></ProtectedRoute>} />
      <Route path="/classes/:id/edit" element={<ProtectedRoute><ClassForm /></ProtectedRoute>} />
      <Route path="/instructors" element={<ProtectedRoute><Instructors /></ProtectedRoute>} />
      <Route path="/instructors/:id" element={<ProtectedRoute><InstructorDetail /></ProtectedRoute>} />
      <Route path="/students" element={<ProtectedRoute><Students /></ProtectedRoute>} />
      <Route path="/students/:id" element={<ProtectedRoute><StudentDetail /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
      <Route path="/companies/:id" element={<ProtectedRoute><CompanyDetail /></ProtectedRoute>} />
      <Route path="/companies/:id/edit" element={<ProtectedRoute><CompanyEdit /></ProtectedRoute>} />
      <Route path="/company-categories" element={<ProtectedRoute><CompanyCategories /></ProtectedRoute>} />
      <Route path="/my-company" element={<ProtectedRoute><MyCompany /></ProtectedRoute>} />
      <Route path="/certificates" element={<ProtectedRoute><Certificates /></ProtectedRoute>} />
      <Route path="/certificate-templates" element={<ProtectedRoute><CertificateTemplates /></ProtectedRoute>} />
      <Route path="/declarations" element={<ProtectedRoute><Declarations /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/email-marketing" element={<ProtectedRoute><EmailMarketing /></ProtectedRoute>} />
      <Route path="/email-marketing/campaigns/:id" element={<ProtectedRoute><CampaignDetail /></ProtectedRoute>} />
      <Route path="/email-settings" element={<ProtectedRoute><EmailSettings /></ProtectedRoute>} />
      <Route path="/audit-logs" element={<ProtectedRoute><AuditLogs /></ProtectedRoute>} />
      <Route path="/expiry-reminders" element={<ProtectedRoute><ExpiryReminders /></ProtectedRoute>} />
      <Route path="/expiry-reminder-settings" element={<ProtectedRoute><ExpiryReminderSettings /></ProtectedRoute>} />
      <Route path="/expiry-reminder-history" element={<ProtectedRoute><ExpiryReminderHistory /></ProtectedRoute>} />
      <Route path="/expiry-reminder-history/:historyId" element={<ProtectedRoute><ExpiryReminderHistoryDetail /></ProtectedRoute>} />
      <Route path="/backup" element={<ProtectedRoute><BackupRestore /></ProtectedRoute>} />
      <Route path="/merge-duplicates" element={<ProtectedRoute><MergeDuplicates /></ProtectedRoute>} />
      <Route path="/merge-history" element={<ProtectedRoute><MergeHistory /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><SecurityManagement /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin-settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NavigationBlockProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </NavigationBlockProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
